import { detectPlatform } from "./Platform";

// 앱 셸(index.html + JS/CSS)을 프리캐시하는 서비스워커(src/sw.js) 등록. 콜드
// 스타트/오프라인에서도 첫 페인트가 즉시 시작되도록 앱 부팅 시(main.tsx) 바로
// 등록한다 — 예전에는 알림 권한을 받은 뒤(push/query.ts)에만 등록해, 알림을
// 거부한 사용자는 서비스워커 자체가 없었다.
// Capacitor 네이티브 셸(android/ios)은 dist 를 로컬 스킴으로 로드하므로 등록하지
// 않는다. Electron 은 dashboard.withtask.app 을 loadURL 하는 일반 웹 컨텍스트라
// 그대로 등록한다.
//
// 업데이트 정책: 새 배포를 사용자에게 알리거나(토스트) 강제로 적용(skip-waiting +
// controllerchange 리로드)하지 않는다. 문서(내비게이션)를 네트워크 우선으로 서빙하므로
// (src/sw.ts) 온라인이면 새로고침/이동 시 항상 최신 index.html → 최신 청크로 정합하게
// 로드되고, 새 서비스워커 자신은 브라우저 기본 수명주기(모든 탭이 닫힌 뒤 다음 콜드
// 스타트)에 조용히 활성화된다. 예전의 감지/폴링·"새 버전으로" 토스트·설정 화면 업데이트
// UI 는 이 정책 전환으로 모두 제거했다.
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) return registrationPromise;

  const platform = detectPlatform();
  const isCapacitorNative = platform === "android" || platform === "ios";
  const isUnsupported = isCapacitorNative || !("serviceWorker" in navigator);

  // vite-plugin-pwa 는 dev 서버에서 SW 를 /sw.js 가 아니라 /dev-sw.js?dev-sw 로만
  // 서빙한다(그리고 ESM 이라 type:"module" 필요). prod 빌드는 /sw.js(classic)로 나오므로
  // 옵션 없이 등록한다. dev 분기가 없으면 dev 에서 SW 등록이 조용히 실패한다.
  const isDev = import.meta.env.DEV;
  const swUrl = isDev ? "/dev-sw.js?dev-sw" : "/sw.js";
  const swOptions: RegistrationOptions | undefined = isDev ? { type: "module" } : undefined;

  registrationPromise = isUnsupported
    ? Promise.resolve(null)
    : navigator.serviceWorker.register(swUrl, swOptions).catch((err) => {
        console.warn("[sw] registration failed", err);
        return null;
      });

  return registrationPromise;
}

/** 로그아웃/계정 삭제 시 다음 사용자가 이전 계정의 캐시된 /api 응답을 보지 않도록 지운다. */
export async function clearApiCache(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration("/sw.js");
    registration?.active?.postMessage({ type: "clear-api-cache" });
  } catch (err) {
    console.warn("[sw] API cache cleanup failed", err);
  }
}
